import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp } from "lucide-react";

export default function ROIChart({ investments, investors }) {
  // Calculate ROI per investor (including unrealized gains)
  const investorROI = investors.map(investor => {
    const investorInvestments = investments.filter(inv => inv.investor_id === investor.id);
    
    // Realized gains from exited investments
    const exitedInvestments = investorInvestments.filter(inv => inv.status === "exited");
    const totalExitInvested = exitedInvestments.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);
    const totalExitValue = exitedInvestments.reduce((sum, inv) => sum + (inv.exit_amount || 0), 0);
    const realizedGain = totalExitValue - totalExitInvested;

    // Unrealized gains from active investments with valuations
    const activeInvestments = investorInvestments.filter(inv => inv.status === "active" && inv.current_valuation);
    const totalActiveInvested = activeInvestments.reduce((sum, inv) => sum + (inv.investment_amount || 0), 0);
    const totalActiveValue = activeInvestments.reduce((sum, inv) => sum + (inv.current_valuation || 0), 0);
    const unrealizedGain = totalActiveValue - totalActiveInvested;

    const totalInvested = totalExitInvested + totalActiveInvested;
    const totalValue = totalExitValue + totalActiveValue;
    const totalGain = realizedGain + unrealizedGain;
    
    const roi = totalInvested > 0 ? ((totalGain / totalInvested) * 100) : null;
    
    return {
      name: investor.name.length > 15 ? investor.name.substring(0, 15) + "..." : investor.name,
      roi: roi !== null ? parseFloat(roi.toFixed(1)) : null,
      totalInvested: totalInvested,
      totalValue: totalValue,
      hasData: totalInvested > 0,
    };
  }).filter(item => item.hasData); // Only show investors with investment data

  const sortedData = investorROI.sort((a, b) => (b.roi || 0) - (a.roi || 0)).slice(0, 10);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-semibold text-slate-900 mb-2">{data.name}</p>
          <p className="text-sm text-slate-600">
            ROI: <span className={`font-bold ${data.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {data.roi?.toFixed(1)}%
            </span>
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Investert: {(data.totalInvested / 1000000).toFixed(2)}M kr
          </p>
          <p className="text-xs text-slate-500">
            Verdi: {(data.totalValue / 1000000).toFixed(2)}M kr
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">ROI per Investor</h3>
          <p className="text-sm text-slate-500">Topp 10 investorer etter avkastning</p>
        </div>
      </div>

      {sortedData.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-slate-500">Ingen avsluttede investeringer ennå</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={sortedData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis 
              dataKey="name" 
              tick={{ fill: "#64748b", fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tick={{ fill: "#64748b", fontSize: 12 }}
              label={{ value: "ROI (%)", angle: -90, position: "insideLeft", style: { fill: "#64748b" } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="roi" radius={[8, 8, 0, 0]}>
              {sortedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.roi >= 0 ? "#10b981" : "#ef4444"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}